import { Layer, LayerProps } from 'shared/map-exports';
import { useMapLayersState } from 'state/useMapLayersState';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { getWatchdutyStaticTileUrl } from 'features/mapping/components/layers/layers.utils';
import { useStaticTileCacheDomain } from 'features/mapping/hooks/useStaticTileCacheDomain';
import {
  BELOW_STREETS_LAYER_ID,
  FONT_NAMES,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { VisibilityToggledSource } from '../VisibilityToggledSource';

const years = 5;
const thisYear = new Date().getFullYear();
const fiveYearsAgo = thisYear - years;

const historicalFireFillStyle = (isDark: boolean): LayerProps => ({
  id: 'historic-perimeters-fill',
  type: 'fill',
  'source-layer': 'perimeters',
  paint: {
    'fill-color': isDark ? '#FFFFFF' : '#777777',
    'fill-opacity': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'year']],
      fiveYearsAgo,
      0.2,
      thisYear,
      isDark ? 0.5 : 0.6,
    ],
  },
});

const historicalFireLabelStyle = (isDark: boolean): LayerProps => ({
  id: 'historic-perimeters-label',
  type: 'symbol',
  'source-layer': 'labels',
  minzoom: 10,
  layout: {
    'text-anchor': 'center',
    'text-field': ['concat', ['get', 'name'], '\n', ['get', 'year']],
    'text-font': FONT_NAMES.bold,
    'text-justify': 'center',
    'text-letter-spacing': 0.2,
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 8, 20, 30],
  },
  paint: {
    'text-color': isDark ? '#ffffff' : '#000000',
    'text-halo-color': isDark ? '#333333' : '#eeeeee',
    'text-halo-width': 1.5,
  },
});

export const HistoricalFirePerimetersLayer = (
  props: MapLayerProps,
): JSX.Element => {
  const { visible } = props;
  const { isDarkBaseLayer } = useMapLayersState();
  const staticTileCacheDomain = useStaticTileCacheDomain();
  const perimeterStyle = historicalFireFillStyle(isDarkBaseLayer);
  const labelStyle = historicalFireLabelStyle(isDarkBaseLayer);

  return (
    <VisibilityToggledSource
      url={getWatchdutyStaticTileUrl(
        'historical_perimeters',
        staticTileCacheDomain,
      )}
      visible={visible}
    >
      <Layer beforeId={BELOW_STREETS_LAYER_ID} {...perimeterStyle} />
      <Layer beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID} {...labelStyle} />
    </VisibilityToggledSource>
  );
};
